import { useEffect, useRef, useState, Suspense } from 'react'
import { motion, useAnimationControls, useInView } from 'framer-motion'
import { EASE_OUT } from '../theme/motion'

// Card motion shared by every card grid (Dashboard, GenericCardPage,
// MyDashboardPage): cascade entrance, crossfade reveal over a skeleton.

// Cards that arrive together cascade in DOM (reading) order; a card that
// arrives alone — scrolled into view — shows at once, never waits its turn.
// Module-level clock, so one grid shares one cascade.
function createCascade({ step, max, window = 120 }) {
  let lastAt = -Infinity
  let streak = 0
  return () => {
    const now = performance.now()
    streak = now - lastAt < window ? streak + 1 : 0
    lastAt = now
    return Math.min(streak * step, max)
  }
}
const cellCascade = createCascade({ step: 0.055, max: 0.4 })
// Tighter than the cells: the card's own enter animations (ring fills, SOC
// bars) start on mount, so the reveal must not lag far behind them.
const revealCascade = createCascade({ step: 0.035, max: 0.25 })

export const CARD_HIDDEN = { opacity: 0, y: 24, scale: 0.985 }

/**
 * Entrance for a grid cell: rises into place the first time it scrolls into
 * view. Spread the result onto the cell's motion.div.
 */
export function useCardEntrance(ref, { margin = '-60px' } = {}) {
  const inView = useInView(ref, { once: true, margin })
  const controls = useAnimationControls()

  useEffect(() => {
    if (!inView) return
    controls.start({
      opacity: 1, y: 0, scale: 1,
      transition: { delay: cellCascade(), duration: 0.6, ease: EASE_OUT },
    })
  }, [inView, controls])

  return { initial: CARD_HIDDEN, animate: controls }
}

// Shimmer placeholder that fills the cell behind the real card, so the
// reveal is a crossfade, never a hard background swap.
export function SkeletonLayer() {
  return (
    <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none bg-surface-container-low">
      <motion.div
        className="absolute inset-0"
        initial={{ x: '-130%' }}
        animate={{ x: '130%' }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.5 }}
        style={{ background: 'linear-gradient(100deg, transparent 35%, rgba(255,255,255,0.04) 50%, transparent 65%)' }}
      />
    </div>
  )
}

// Crossfades the real card in over the skeleton, in cascade with its
// neighbours. Opacity only — no transform/filter, so it never leaves a
// containing block that would break fixed descendants.
export function CardReveal({ children, onMount, onRevealed }) {
  const controls = useAnimationControls()
  const cb = useRef({ onMount, onRevealed })
  useEffect(() => { cb.current = { onMount, onRevealed } })

  useEffect(() => {
    cb.current.onMount?.()
    controls
      .start({ opacity: 1, transition: { delay: revealCascade(), duration: 0.5, ease: EASE_OUT } })
      .then(() => cb.current.onRevealed?.())
  }, [controls])

  return (
    <motion.div className="relative z-[1] h-full" initial={{ opacity: 0 }} animate={controls}>
      {children}
    </motion.div>
  )
}

/**
 * Skeleton → card crossfade for a lazily loaded card. The skeleton unmounts
 * once the card is fully in, so no shimmer loop keeps running behind it.
 * `minHeight` holds the cell open only until the card has mounted.
 */
export function CardBody({ children, minHeight = 180 }) {
  const [mounted, setMounted] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="relative h-full" style={{ minHeight: mounted ? undefined : minHeight }}>
      {!revealed && <SkeletonLayer />}
      <Suspense fallback={null}>
        <CardReveal onMount={() => setMounted(true)} onRevealed={() => setRevealed(true)}>
          {children}
        </CardReveal>
      </Suspense>
    </div>
  )
}
